document.addEventListener('DOMContentLoaded', () => {
  // Functional utility for DOM element selection
  const getElement = selector => document.querySelector(selector);
  const getAllElements = selector => [...document.querySelectorAll(selector)];

  // Select elements using functional approach
  const modal = getElement('.color-collection__modal');
  const closeButton = getElement('.color-collection__modal-close');
  const productButtons = getAllElements('.color-collection__product');
  const selectedColorImage = getElement('.selected-color-image');
  const slideCloseButtons = getAllElements('.color-collection__main-slide-close-button');

  // Create a map to store color data for each product
  const colorsData = new Map();

  // Detect if we're on mobile
  const isMobile = () => window.innerWidth < 768;

  // Collect all color data on load
  const initializeColorData = () => {
    // Look for hidden color data elements that store all images
    const colorDataElements = getAllElements('.color-collection__product-data');

    // Process each color's data
    colorDataElements.forEach(colorDataElement => {
      const colorIndex = colorDataElement.getAttribute('data-color-index');
      const colorName = colorDataElement.getAttribute('data-color-name') || '';

      // Get main image data element for this color (we only need the first one)
      const imageDataElement = colorDataElement.querySelector('.color-collection__product-image-data');

      // Extract image data
      const colorImage = imageDataElement ? {
        src: imageDataElement.getAttribute('data-src'),
        alt: imageDataElement.getAttribute('data-alt'),
        mainSrc: imageDataElement.getAttribute('data-main-src') || imageDataElement.getAttribute('data-src')
      } : null;

      // Store color data
      colorsData.set(colorIndex, {
        name: colorName,
        image: colorImage
      });
    });

    // If no color data was found in hidden storage, fall back to product data
    if (colorsData.size === 0) {
      productButtons.forEach(button => {
        const colorIndex = button.getAttribute('data-color-index');
        const colorName = button.querySelector('.color-collection__product-color')?.textContent.trim() || '';

        // Get the main image in the product
        const mainImage = button.querySelector('img');
        const colorImage = mainImage ? {
          src: mainImage.src,
          alt: mainImage.alt,
          mainSrc: mainImage.src // Fallback to regular image if main image not available
        } : null;

        colorsData.set(colorIndex, {
          name: colorName,
          image: colorImage
        });
      });
    }
  };

  // Toggle modal visibility with functional approach
  const toggleModal = (isVisible) => {
    if (isVisible) {
      modal.style.display = 'block';
      // Force reflow before adding the class for the transition to take effect
      void modal.offsetWidth;
      modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    } else {
      modal.classList.remove('is-active');
      document.body.style.overflow = '';
      // Wait for transition to complete before hiding
      setTimeout(() => {
        if (!modal.classList.contains('is-active')) {
          modal.style.display = 'none';
        }
      }, 300);
    }
  };

  // Smooth scroll to element
  const smoothScrollToElement = (element) => {
    if (!element) return;

    const scrollOptions = {
      behavior: 'smooth',
      block: 'center'
    };

    element.scrollIntoView(scrollOptions);
  };

  // Initial state
  toggleModal(false);
  initializeColorData();

  // Handle close modal
  const handleCloseModal = () => {
    toggleModal(false);

    // Reset active states when modal is closed
    getAllElements('.color-collection__product-button').forEach(btn => {
      btn.classList.remove('active');
      btn.classList.remove('reduced-opacity');

      // Remove the grid HTML when closing
      const parentContainer = btn.closest('.color-collection__product-container');
      const existingGrid = parentContainer ? parentContainer.querySelector('.grid') : null;
      if (existingGrid) {
        existingGrid.remove();
      }
    });

    getAllElements('.color-collection__grid-item').forEach(item =>
      item.classList.remove('active')
    );
  };

  // Handle product click to open modal and display product
  const handleProductClick = (event) => {
    const product = event.currentTarget;
    const colorIndex = product.getAttribute('data-color-index');

    // Get the color data for this product
    const colorData = colorsData.get(colorIndex);

    // Open modal
    toggleModal(true);

    // Set the main image immediately if on desktop
    if (!isMobile() && selectedColorImage) {
      // Find the product image as fallback
      const productImage = product.querySelector('img');
      const fallbackSrc = productImage ? productImage.src : '';

      // Use the main image from color data, falling back to regular image if not available
      const mainImageSrc = colorData?.image?.mainSrc || colorData?.image?.src || fallbackSrc;
      const imageAlt = colorData?.image?.alt || '';

      selectedColorImage.src = mainImageSrc;
      selectedColorImage.alt = imageAlt;
    }

    // Select the corresponding product button in the modal after a small delay
    setTimeout(() => {
      // Find the product button with matching color index
      const targetButton = getAllElements('.color-collection__product-button').find(btn => {
        return btn.getAttribute('data-color-index') === colorIndex;
      });

      // Simulate a click on the target button if found
      if (targetButton) {
        targetButton.click();
      }
    }, 100);
  };

  // Handle product button click in the modal
  const handleProductButtonClick = (event) => {
    const image = event.currentTarget;
    const parentContainer = image.closest('.color-collection__product-container');
    const colorIndex = parentContainer.getAttribute('data-color-index');
    const isAlreadyActive = image.classList.contains('active');
    const gridItem = image.closest('.color-collection__grid-item');

    // Get the color data from our stored map
    const colorData = colorsData.get(colorIndex);

    // Get the image source from the clicked image as fallback
    const imageSrc = image.src;

    // Remove active state from all images and grid items
    getAllElements('.color-collection__product-button').forEach(img => {
      img.classList.remove('active');
      // Remove the grid HTML from all images
      const container = img.closest('.color-collection__product-container');
      const existingGrid = container ? container.querySelector('.grid') : null;
      if (existingGrid) {
        existingGrid.remove();
      }
    });

    getAllElements('.color-collection__grid-item').forEach(item =>
      item.classList.remove('active')
    );

    // Toggle active state for clicked image
    if (!isAlreadyActive) {
      image.classList.add('active');
      gridItem.classList.add('active');

      // Only update the selected image on desktop
      if (!isMobile()) {
        if (selectedColorImage) {
          // Use the main image from color data, falling back to regular image if not available
          const mainImageSrc = colorData?.image?.mainSrc || colorData?.image?.src || imageSrc;
          const imageAlt = colorData?.image?.alt || '';

          selectedColorImage.src = mainImageSrc;
          selectedColorImage.alt = imageAlt;
        }
      }

      // Scroll to the active item on mobile
      if (isMobile()) {
        setTimeout(() => smoothScrollToElement(gridItem), 0);
      } else {
        setTimeout(() => smoothScrollToElement(gridItem), 50);
      }
    }

    // Check if any button is active after toggling
    const hasActiveButton = getAllElements('.color-collection__product-button').some(btn =>
      btn.classList.contains('active')
    );

    // Apply opacity classes based on whether any button is active
    getAllElements('.color-collection__product-button').forEach(btn => {
      btn.classList.remove('reduced-opacity');
      if (hasActiveButton && !btn.classList.contains('active')) {
        btn.classList.add('reduced-opacity');
      }
    });
  };

  // Handle ESC key press
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleCloseModal();
    }
  };

  // Attach event listeners
  closeButton?.addEventListener('click', handleCloseModal);
  document.addEventListener('keydown', handleKeyDown);

  // Add click event listeners to slide close buttons
  slideCloseButtons.forEach(button => {
    button.addEventListener('click', handleCloseModal);
  });

  // Use event delegation to handle dynamically added close buttons
  document.addEventListener('click', event => {
    if (event.target.closest('.color-collection__main-slide-close-button')) {
      handleCloseModal();
    }
  });

  // Add click handlers to color products in the section to open modal
  productButtons.forEach(product => {
    product.addEventListener('click', handleProductClick);
  });

  // Add click handlers to product buttons in the modal
  getAllElements('.color-collection__product-button').forEach(button => {
    button.addEventListener('click', handleProductButtonClick);
  });

  // Mouse hover effect for products in the main grid
  productButtons.forEach(product => {
    product.addEventListener('mouseenter', () => {
      // Find all products inside color-collection
      const allProducts = document.querySelectorAll('.color-collection__product');

      // Add faded class to all products except the hovered one
      allProducts.forEach(item => {
        if (item !== product) {
          item.classList.add('color-collection__product--faded');
        }
      });
    });

    product.addEventListener('mouseleave', () => {
      // Find all products inside color-collection
      const allProducts = document.querySelectorAll('.color-collection__product');

      // Remove faded class from all products
      allProducts.forEach(item => {
        item.classList.remove('color-collection__product--faded');
      });
    });
  });
});